/**
 * Record updates for one organisation: the housekeeping suggestions, each
 * with its before-value, plus Apply / Skip / Undo.
 *
 * Reads only what the library already loads (documents, governance rows,
 * owners, people, frameworks, the company profile), all scoped to the acting
 * organisation. A decision refreshes those reads too, so the next row's
 * before-value is the value the last Apply wrote.
 *
 * @module state/recordUpdates
 */
import { useCallback, useMemo } from "react";

import {
  runRecordActions,
  toRecordUpdate,
  type RecordAction,
  type RecordOutcome,
  type RecordSources,
  type RecordUpdate,
} from "../components/regulation/recordUpdates/recordUpdates.logic";
import {
  useGovernanceFrameworks,
  useOrgGovernance,
  useOrganizationPeople,
  useOwnersByPolicy,
  useRecommendations,
  type GovernanceFrameworkList,
  type OrgPolicyGovernance,
  type OrganizationPerson,
  type Recommendation,
} from "./governance";
import { useOrgProfile, type OrgProfileUnavailableReason } from "./orgProfile";
import { policyDisplayName, usePolicies, type PolicyListSnapshot } from "./policies";

export interface RecordSourceInputs {
  readonly library: PolicyListSnapshot | null;
  readonly governance: ReadonlyArray<OrgPolicyGovernance>;
  readonly owners: ReadonlyMap<string, ReadonlyArray<OrganizationPerson>>;
  readonly people: ReadonlyArray<OrganizationPerson>;
  readonly frameworks: GovernanceFrameworkList | null;
  /** Undefined until the profile loads, so a profile before-value reads "Unknown", not "Not set". */
  readonly organizationContext: string | null | undefined;
}

/** The lookups the arrangement reads, from the library's own snapshots. */
export function buildRecordSources(input: RecordSourceInputs): RecordSources {
  const people = new Map<string, string>();
  for (const person of input.people) people.set(person.email.toLowerCase(), person.name?.trim() || person.email);
  for (const list of input.owners.values())
    for (const person of list) if (!people.has(person.email.toLowerCase())) people.set(person.email.toLowerCase(), person.name?.trim() || person.email);
  return {
    documents: new Map(
      (input.library?.policies ?? []).map((policy) => [
        policy.id,
        {
          name: policyDisplayName(policy),
          title: policy.title ?? null,
          documentKind: policy.documentKind ?? "policy",
          jurisdictionSlugs: policy.jurisdictionSlugs,
          governanceFrameworkId: policy.governanceFrameworkId ?? null,
        },
      ]),
    ),
    governance: new Map(
      input.governance.map((row) => [row.policyId, { title: row.title, department: row.department, reviewFrequency: row.reviewFrequency }]),
    ),
    ownerEmails: new Map([...input.owners.entries()].map(([policyId, list]) => [policyId, list.map((person) => person.email)])),
    frameworks: new Map((input.frameworks?.frameworks ?? []).map((framework) => [framework.id, framework.name])),
    jurisdictions: new Map((input.library?.jurisdictions ?? []).map((jurisdiction) => [jurisdiction.slug, jurisdiction.name])),
    people,
    organizationContext: input.organizationContext,
  };
}

/** Housekeeping suggestions as rows, newest first, gaps left out. */
export function recordUpdatesFrom(recommendations: ReadonlyArray<Recommendation>, sources: RecordSources): ReadonlyArray<RecordUpdate> {
  return recommendations.flatMap((suggestion) => {
    const row = toRecordUpdate(suggestion, sources);
    return row ? [row] : [];
  });
}

export interface RecordUpdatesView {
  readonly updates: ReadonlyArray<RecordUpdate>;
  /** True once the suggestions and the library have loaded. */
  readonly ready: boolean;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly canManage: boolean;
  readonly refresh: () => void;
  /** Run one action over rows, one request at a time; reports each row. */
  readonly run: (action: RecordAction, updates: ReadonlyArray<RecordUpdate>) => Promise<ReadonlyArray<RecordOutcome>>;
}

export function useRecordUpdates(organizationId: string | null): RecordUpdatesView {
  const recommendations = useRecommendations(organizationId);
  const library = usePolicies(organizationId);
  const governance = useOrgGovernance(organizationId);
  const owners = useOwnersByPolicy(organizationId);
  const people = useOrganizationPeople(organizationId);
  const frameworks = useGovernanceFrameworks(organizationId);
  const profile = useOrgProfile(organizationId);

  const sources = useMemo(
    () =>
      buildRecordSources({
        library: library.snapshot,
        governance: governance.snapshot?.policies ?? [],
        owners: owners.byPolicyId,
        people: people.snapshot?.people ?? [],
        frameworks: frameworks.snapshot,
        organizationContext: profile.profile ? profile.profile.organizationContext : undefined,
      }),
    [library.snapshot, governance.snapshot, owners.byPolicyId, people.snapshot, frameworks.snapshot, profile.profile],
  );
  const updates = useMemo(
    () => recordUpdatesFrom(recommendations.snapshot?.recommendations ?? [], sources),
    [recommendations.snapshot, sources],
  );

  const refreshers = [library.refresh, governance.refresh, owners.refresh, frameworks.refresh, profile.refresh] as const;
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- each refresher is itself stable
  const refreshRecords = useCallback(() => refreshers.forEach((refreshOne) => refreshOne()), refreshers);

  const { refresh: refreshRecommendations } = recommendations;
  const refresh = useCallback(() => {
    refreshRecommendations();
    refreshRecords();
  }, [refreshRecommendations, refreshRecords]);

  const { decide } = recommendations;
  const run = useCallback<RecordUpdatesView["run"]>(
    async (action, rows) => {
      const outcomes = await runRecordActions(action, rows, (id, decision) =>
        decide(id, decision.decision, decision.note === undefined ? undefined : { note: decision.note }),
      );
      // Apply and Undo change the record itself, not only the suggestion.
      if (action !== "skip" && outcomes.some((outcome) => outcome.error === null)) refreshRecords();
      return outcomes;
    },
    [decide, refreshRecords],
  );

  return {
    updates,
    ready: recommendations.snapshot !== null && library.snapshot !== null,
    unavailableReason: recommendations.unavailableReason ?? library.unavailableReason,
    canManage: profile.canManage,
    refresh,
    run,
  };
}
